import { request } from "./request";

/**
 * Utilidades para las categorías de productos (recurso `grupos`).
 *
 * `GET grupos` devuelve el listado plano y `GET grupos?arbol=1` devuelve solo las
 * raíces con sus `hijos` anidados recursivamente. La forma de cada categoría es la
 * misma en ambos casos; la clave `hijos` solo existe en la respuesta del árbol.
 *
 * `ruta` es el breadcrumb ya armado por el backend ("Bebidas > Gaseosas"), así que
 * se usa tal cual para mostrar la categoría en listados y chips.
 */

/**
 * El index de grupos devuelve { data: [...] } (resource collection) mientras que
 * store/update/destroy devuelven { success, data, code }. Esto normaliza ambas.
 */
export const gruposDeRespuesta = (respuesta) => {
	if (!respuesta) return [];

	if (Array.isArray(respuesta)) return respuesta;

	if (Array.isArray(respuesta.data)) return respuesta.data;

	return [];
};

export const getArbolCategorias = async (signal = null) => {
	const respuesta = await request("grupos?arbol=1", signal);

	return gruposDeRespuesta(respuesta);
};

/** Recorre el árbol en preorden y devuelve una lista plana con el nivel de cada categoría. */
export const aplanarArbol = (arbol = [], nivel = 0) =>
	(arbol || []).reduce(
		(acc, categoria) => [
			...acc,
			{ ...categoria, nivel },
			...aplanarArbol(categoria.hijos, nivel + 1),
		],
		[]
	);

/** Minúsculas y sin acentos, para comparar nombres al buscar. */
export const normalizarTexto = (texto = "") =>
	String(texto)
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase()
		.trim();

/**
 * Poda el árbol a las categorías cuyo nombre contiene `termino`. Una que coincide
 * se conserva con todas sus hijas (buscar "Bebidas" muestra sus subcategorías);
 * una que no coincide sólo queda si alguna descendiente coincide, para que se vea
 * dónde está.
 */
export const filtrarArbol = (arbol = [], termino = "") => {
	const buscado = normalizarTexto(termino);
	if (!buscado) return arbol || [];

	return (arbol || []).reduce((acc, categoria) => {
		if (normalizarTexto(categoria.nombre).includes(buscado)) {
			return [...acc, categoria];
		}

		const hijos = filtrarArbol(categoria.hijos, buscado);

		return hijos.length > 0 ? [...acc, { ...categoria, hijos }] : acc;
	}, []);
};

/** Busca una categoría dentro del árbol por id. */
export const buscarEnArbol = (arbol = [], id) => {
	if (id === null || id === undefined) return null;

	for (const categoria of arbol || []) {
		if (categoria.id === parseInt(id)) return categoria;

		const encontrada = buscarEnArbol(categoria.hijos, id);

		if (encontrada) return encontrada;
	}

	return null;
};

/** Ids de una categoría junto con los de todos sus descendientes. */
export const idsDeRama = (categoria) => {
	if (!categoria) return [];

	return [
		categoria.id,
		...(categoria.hijos || []).reduce(
			(acc, hijo) => [...acc, ...idsDeRama(hijo)],
			[]
		),
	];
};

/** Nombre corto, para cuando la jerarquía ya se ve (árbol, chips). */
export const etiquetaCategoria = (categoria) =>
	categoria?.nombre || "";

/**
 * Breadcrumb completo que arma el backend ("Bebidas > Gaseosas"). Se usa donde no
 * hay contexto jerárquico: opciones de un dropdown plano, tooltips y resúmenes,
 * donde dos subcategorías con el mismo nombre serían indistinguibles.
 */
export const rutaCategoria = (categoria) =>
	categoria?.ruta || categoria?.nombre || "";

/**
 * Opciones a partir del árbol. El `label` es la ruta completa para que el buscador
 * de react-select encuentre una subcategoría escribiendo el nombre del padre;
 * `etiquetaIndentada` es para los `<select>` nativos, que no aceptan markup.
 *
 * @param {array} arbol - Respuesta de `grupos?arbol=1`.
 * @param {array} excluir - Ids que no deben aparecer (ej. la rama propia al elegir padre).
 */
export const opcionesDeArbol = (arbol = [], excluir = []) =>
	aplanarArbol(arbol)
		.filter((categoria) => !excluir.includes(categoria.id))
		.map((categoria) => ({
			value: categoria.id,
			label: rutaCategoria(categoria),
			nombre: categoria.nombre,
			ruta: rutaCategoria(categoria),
			nivel: categoria.nivel,
			// Espacios duros: los <option> nativos colapsan los espacios normales.
			etiquetaIndentada: `${"   ".repeat(categoria.nivel)}${
				categoria.nivel > 0 ? "└ " : ""
			}${categoria.nombre}`,
		}));

/**
 * Categorías de un producto. Mientras la migración a `categorias` es incremental,
 * los endpoints que todavía no la incluyen siguen trayendo `grupo` (la principal).
 */
export const categoriasDeProducto = (producto) => {
	if (producto?.categorias?.length) return producto.categorias;

	return producto?.grupo ? [producto.grupo] : [];
};

export const idsDeCategorias = (categorias = []) =>
	(categorias || []).map((categoria) => categoria.id);
